import { Link } from '@router'
import type { Activity } from '@hewey/shared'
import { useImageSrc } from '@hooks/useImageSrc.ts'
import { ActivityImage } from './ActivityImage.tsx'
import { ACTIVITY_TYPE_META, COST_META } from './activityLabels.ts'
import styles from './ActivityCard.module.css'

type Props = {
	activity: Activity
	// The View-all list uses a horizontal thumbnail + title row. Everything else
	// (Explore rows, search results) uses the image-forward feature card.
	compact?: boolean
	// Given, the card becomes a button that calls this instead of a link to the
	// detail page — the Needs-tagging list tags in place rather than navigating.
	onSelect?: (() => void) | undefined
}

export const ActivityCard = ({
	activity,
	compact = false,
	onSelect,
}: Props) => {
	const imageSrc = useImageSrc('activities', activity.id, activity.photoUrl)
	// Several types join with a dot. The venue's friendlyName stays off the card
	// — it is usually just the activity title again — but the town it sits in is
	// coarser and does add something the title doesn't carry.
	const typeLabel =
		activity.types.length > 0
			? activity.types.map((t) => ACTIVITY_TYPE_META[t].label).join(' · ')
			: null
	const cost = activity.cost ? COST_META[activity.cost].label : null
	const where =
		activity.location.kind === 'home' ? 'Home' : activity.location.town

	const className =
		`${styles.card} ${compact ? styles.compact : styles.feature}`.trim()

	let body
	if (compact) {
		const meta = [where, typeLabel, cost].filter(Boolean).join(' · ')
		body = (
			<>
				<div className={styles.thumb}>
					<ActivityImage
						src={imageSrc}
						seed={activity.id}
						type={activity.types[0] ?? null}
						className={styles.thumbImg}
					/>
				</div>
				<div className={styles.compactBody}>
					<h3 className={styles.compactTitle}>{activity.name}</h3>
					{meta ? <p className={styles.compactMeta}>{meta}</p> : null}
				</div>
			</>
		)
	} else {
		// Town leads, so on a narrow grid card it is the type that gets
		// ellipsised.
		const main = [where, typeLabel].filter(Boolean).join(' · ')
		body = (
			<>
				<div className={styles.featureImage}>
					<ActivityImage
						src={imageSrc}
						seed={activity.id}
						type={activity.types[0] ?? null}
						className={styles.image}
					/>
				</div>
				<div className={styles.featureBody}>
					<h3 className={styles.featureTitle}>{activity.name}</h3>
					{main || cost ? (
						<div className={styles.featureMeta}>
							<span className={styles.metaMain}>{main}</span>
							{cost ? (
								<span className={styles.metaCost}>{cost}</span>
							) : null}
						</div>
					) : null}
				</div>
			</>
		)
	}

	return onSelect ? (
		<button type="button" className={className} onClick={onSelect}>
			{body}
		</button>
	) : (
		<Link href={`/activities/${activity.id}`} className={className}>
			{body}
		</Link>
	)
}
